const COMPARISON_PICKER_STORAGE_KEY = 'product-comparison-picker';
const COMPARISON_TRANSFER_STORAGE_KEY = 'product-comparison-transfer';
const COMPARISON_PICKER_MAX = 3;

class ProductComparisonPickerStore {
  static getProducts() {
    try {
      const saved = JSON.parse(sessionStorage.getItem(COMPARISON_PICKER_STORAGE_KEY) || '{}');
      return Array.isArray(saved.products) ? saved.products : [];
    } catch (error) {
      return [];
    }
  }

  static saveProducts(products) {
    sessionStorage.setItem(
      COMPARISON_PICKER_STORAGE_KEY,
      JSON.stringify({
        products,
        handles: products.map((product) => product.handle).filter(Boolean),
      })
    );
  }

  static addProduct(product) {
    const products = ProductComparisonPickerStore.getProducts();

    if (products.some((item) => String(item.id) === String(product.id))) {
      return { ok: true };
    }

    if (products.length >= COMPARISON_PICKER_MAX) {
      return { ok: false, reason: 'max' };
    }

    ProductComparisonPickerStore.saveProducts([...products, product]);
    return { ok: true };
  }

  static removeProduct(productId) {
    const products = ProductComparisonPickerStore.getProducts().filter(
      (item) => String(item.id) !== String(productId)
    );
    ProductComparisonPickerStore.saveProducts(products);
  }

  static clearAll() {
    ProductComparisonPickerStore.saveProducts([]);
  }

  static transferToComparison() {
    const products = ProductComparisonPickerStore.getProducts();

    sessionStorage.setItem(
      COMPARISON_TRANSFER_STORAGE_KEY,
      JSON.stringify({
        handles: products.map((product) => product.handle).filter(Boolean),
      })
    );

    ProductComparisonPickerStore.clearAll();
  }
}

window.ProductComparisonPickerStore = ProductComparisonPickerStore;

if (!customElements.get('product-comparison-picker')) {
  class ProductComparisonPicker extends HTMLElement {
    connectedCallback() {
      this.compareUrl = this.dataset.compareUrl || '/pages/karsilastir';
      this.itemsEl = this.querySelector('[data-comparison-picker-items]');
      this.panelEl = this.querySelector('[data-comparison-picker-panel]');
      this.summaryEl = this.querySelector('[data-comparison-picker-summary]');
      this.toggleButton = this.querySelector('[data-comparison-picker-toggle-panel]');
      this.isExpanded = true;

      this.strings = {
        heading: this.dataset.heading || 'Compare up to 3 products',
        compare: this.dataset.compareLabel || 'Compare',
        clearAll: this.dataset.clearAllLabel || 'Delete all',
        maxReached: this.dataset.maxReached || 'Maximum 3 products',
        remove: this.dataset.removeLabel || 'Remove',
      };

      this.boundChange = this.handleChange.bind(this);
      this.boundClick = this.handleClick.bind(this);

      document.addEventListener('change', this.boundChange);
      this.addEventListener('click', this.boundClick);
      this.observeProductGrid();
      this.observeGridContainer();
      this.render();
      this.syncCheckboxes();
    }

    disconnectedCallback() {
      document.removeEventListener('change', this.boundChange);
      this.removeEventListener('click', this.boundClick);
      this.gridObserver?.disconnect();
      this.containerObserver?.disconnect();
    }

    observeGridContainer() {
      const container = document.getElementById('ProductGridContainer');
      if (!container) return;

      this.containerObserver = new MutationObserver(() => {
        this.observeProductGrid();
        this.syncCheckboxes();
      });

      this.containerObserver.observe(container, { childList: true, subtree: true });
    }

    observeProductGrid() {
      this.gridObserver?.disconnect();

      const grid = document.getElementById('product-grid');
      if (!grid) return;

      this.gridObserver = new MutationObserver(() => {
        this.syncCheckboxes();
      });

      this.gridObserver.observe(grid, { childList: true, subtree: true });
    }

    handleChange(event) {
      const input = event.target.closest('[data-comparison-picker-input]');
      if (!input) return;

      event.stopPropagation();

      const product = this.getProductFromInput(input);

      if (input.checked) {
        const result = ProductComparisonPickerStore.addProduct(product);
        if (!result.ok) {
          input.checked = false;
          if (result.reason === 'max') {
            window.alert(this.strings.maxReached);
          }
          return;
        }
      } else {
        ProductComparisonPickerStore.removeProduct(product.id);
      }

      this.updateVisibility();
    }

    resetPicker() {
      ProductComparisonPickerStore.clearAll();
      this.updateVisibility();
    }

    updateVisibility() {
      this.render();
      this.syncCheckboxes();
    }

    handleClick(event) {
      const removeButton = event.target.closest('[data-comparison-picker-remove]');
      if (removeButton) {
        event.preventDefault();
        ProductComparisonPickerStore.removeProduct(removeButton.dataset.comparisonPickerRemove);
        this.updateVisibility();
        return;
      }

      const clearButton = event.target.closest('[data-comparison-picker-clear-all]');
      if (clearButton) {
        event.preventDefault();
        this.resetPicker();
        return;
      }

      const compareButton = event.target.closest('[data-comparison-picker-compare]');
      if (compareButton) {
        event.preventDefault();
        const products = ProductComparisonPickerStore.getProducts();
        if (!products.length) return;

        ProductComparisonPickerStore.transferToComparison();
        this.resetPicker();
        window.location.assign(this.compareUrl);
        return;
      }

      const toggleButton = event.target.closest('[data-comparison-picker-toggle-panel]');
      if (toggleButton) {
        event.preventDefault();
        this.isExpanded = !this.isExpanded;
        this.panelEl?.classList.toggle('is-collapsed', !this.isExpanded);
        toggleButton.setAttribute('aria-expanded', String(this.isExpanded));
      }
    }

    getProductFromInput(input) {
      return {
        id: input.dataset.productId,
        handle: input.dataset.productHandle,
        title: input.dataset.productTitle,
        image: input.dataset.productImage || '',
        url: input.dataset.productUrl || '',
        price: input.dataset.productPrice || '',
      };
    }

    syncCheckboxes() {
      const products = ProductComparisonPickerStore.getProducts();
      const selectedIds = new Set(products.map((product) => String(product.id)));
      const isFull = products.length >= COMPARISON_PICKER_MAX;

      document.querySelectorAll('[data-comparison-picker-input]').forEach((input) => {
        const isSelected = selectedIds.has(String(input.dataset.productId));
        input.checked = isSelected;
        input.disabled = !isSelected && isFull;
        input.closest('.product-comparison-picker__control')?.classList.toggle('is-disabled', !isSelected && isFull);
      });
    }

    render() {
      const products = ProductComparisonPickerStore.getProducts();
      const hasProducts = products.length > 0;
      const bar = this.querySelector('.product-comparison-picker__bar');

      this.hidden = !hasProducts;

      if (bar) {
        bar.hidden = !hasProducts;
      }

      document.body.classList.toggle('has-comparison-picker', hasProducts);

      if (this.summaryEl) {
        this.summaryEl.textContent = this.strings.heading;
      }

      if (!this.itemsEl) return;

      if (!hasProducts) {
        this.itemsEl.innerHTML = '';
        return;
      }

      this.itemsEl.innerHTML = products
        .map(
          (product) => `
            <div class="product-comparison-picker__item">
              ${
                product.image
                  ? `<img src="${product.image}" alt="" class="product-comparison-picker__item-image" loading="lazy" width="80" height="80">`
                  : `<span class="product-comparison-picker__item-image product-comparison-picker__item-image--placeholder" aria-hidden="true"></span>`
              }
              <div class="product-comparison-picker__item-body">
                <a href="${product.url}" class="product-comparison-picker__item-title link_hover">
                  ${product.title}
                </a>
                ${product.price ? `<p class="product-comparison-picker__item-price">${product.price}</p>` : ''}
                <button
                  type="button"
                  class="product-comparison-picker__item-remove link link--text"
                  data-comparison-picker-remove="${product.id}"
                >
                  ${this.strings.remove}
                </button>
              </div>
            </div>
          `
        )
        .join('');
    }
  }

  customElements.define('product-comparison-picker', ProductComparisonPicker);
}
